import Joi, { bool } from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getColleges,
  getPageBlocks,
  getUserTypes,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateDescription,
  IDescriptionData,
} from "../../../../../models/master/descriptions";
import { createDescription } from "../../../../../action/masters/descriptionActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";

const DescriptionCreate = (props: ICreateDescription) => {
  const {
    createDescription,
    setShowCreate,
    getColleges,
    colleges,
    getPageBlocks,
    pageBlocks,
    getUserTypes,
    userTypes,
    getAllDescription,
    getUserPageBlocks,
  } = props;
  const navigate = useNavigate();

  const initialData: IDescriptionData = {
    id: null,
    title: "",
    description: "",
    college: "",
    PageBlockName: "",
    image: "",
    userType: null,
    pageBlockId: null,
    collegeId: null,
    displayOrNot: true,
  };

  // states
  const [data, setData] = useState(initialData);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];

    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value));
      _data.pageBlockId = null;
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      const fileType = selectedFile.type;
      const validFileTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/ico",
        "image/svg+xml",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an image.",
        });
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  // Updated Joi schema for validation
  const schema = Joi.object().keys({
    id: Joi.number().optional().allow(null),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    description: Joi.string().required().label("Description is required"),
    pageBlockId: Joi.number().greater(0).required().label("Select Page Block"),
    collegeId: Joi.number().greater(0).required().label("Select University Management"),
  });

  const validationData = {
    ...data,
  };

  const onSave = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("userType", data.userType?.toString() || "");
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("collegeId", data.collegeId?.toString() || "");
      formData.append("pageBlockId", data.pageBlockId?.toString() || "");
      formData.append("displayOrNot", data.displayOrNot ? "true" : "false");
      setLoading(true);
      setIsDisabled(true);
      try {
        await createDescription(formData, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllDescription();
      setShowCreate(false);
    }
  };

  //setting warning alert on cancel
  const onCancel = async () => {
    setShowAlertCancel(true);
    setAlert(alertConstants.cancelCheck);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collegesData, pageBlocksData] = await Promise.all([
          getColleges(),
          getUserTypes(),
        ]);

        if (collegesData && collegesData.length > 0) {
          setData((data) => ({
            ...data,
            collegeId: collegesData[0]?.id,
          }));
        }
        if (pageBlocksData && pageBlocksData.length > 0) {
          setData((data) => ({
            ...data,
            pageBlockId: pageBlocksData[0]?.id,
          }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setPageBlocks([]));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Add Description
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowCreate(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-lg-12 form-group" id="collegewrap">
                <FloatingLabelDropDown
                  label={"University Management"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.collegeId}
                  name={"collegeId"}
                  optionsList={sortAlphabetically(colleges)}
                />
                {errors?.collegeId && (
                  <div className="text-danger">{errors.collegeId}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"User Type "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.userType}
                  name={"userType"}
                  optionsList={sortAlphabetically(
                    userTypes?.filter((type) => type.id !== UserTypes.Freshman)
                  )}
                />
                {errors?.userType && (
                  <div className="text-danger">{errors.userType}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"Page-Block "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.pageBlockId}
                  name={"pageBlockId"}
                  optionsList={sortAlphabetically(pageBlocks)}
                />
                {errors?.pageBlockId && (
                  <div className="text-danger">{errors.pageBlockId}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelTextInput
                  label={"Title"}
                  onChange={onChange}
                  isMandatory={false}
                  value={data.title}
                  name={"title"}
                  maxLength={70}
                />
              </div>
              <label className="profile_details_text">
                Description
                <i className="label-asterisk text-danger"> * </i>
              </label>
              <div className="col-lg-12 form-group">
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                >
                  {" "}
                </textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>
              <div className="col-lg-6 form-group">
                <label className="profile_details_text">Visibility</label>
                <div className="switches-container">
                  <input
                    type="radio"
                    id="switchShow"
                    name="visibilitySwitch"
                    value="true"
                    checked={data.displayOrNot === true}
                    onChange={() => setData({ ...data, displayOrNot: true })}
                  />
                  <label htmlFor="switchShow">Show</label>
                  <input
                    type="radio"
                    id="switchHide"
                    name="visibilitySwitch"
                    value="false"
                    checked={data.displayOrNot === false}
                    onChange={() => setData({ ...data, displayOrNot: false })}
                  />
                  <label htmlFor="switchHide">Hide</label>
                  <div className="switch-wrapper">
                    <div className="switch">
                      <div>Show</div>
                      <div>Hide</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowCreate(false)}
              >
                Close
              </button>
            </div>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  createDescription: (data: any, callBack: Function) =>
    createDescription(data, callBack),
  getColleges: () => getColleges(),
  getPageBlocks: () => getPageBlocks(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number) => getUserPageBlocks(userType),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DescriptionCreate);
